import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Lightning, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/auth/forgot-password', null, { params: { email } });
      setIsSubmitted(true);
      toast.success('Reset link sent!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-[4.5rem] w-auto" />
          </Link>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-8 shadow-sm">
          {isSubmitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#10B981]" weight="fill" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A192F] tracking-tight mb-2">Check Your Email</h2>
              <p className="text-[#4B5563] mb-6">
                If an account exists for {email}, you will receive a password reset link shortly.
              </p>
              <Link to="/login">
                <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none px-6 py-3 font-medium">
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-2">Forgot Password?</h2>
                <p className="text-[#4B5563]">Enter your email to receive a reset link</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-[#0A192F] mb-2 block">Email Address</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@company.com"
                      className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none h-12 font-medium transition-transform hover:-translate-y-0.5"
                  data-testid="submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <p className="mt-6 text-center text-[#4B5563]">
                Remember your password?{' '}
                <Link to="/login" className="text-[#0055FF] font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>

        <Link to="/" className="block mt-6 text-center text-[#4B5563] text-sm hover:text-[#0A192F]">
          &larr; Back to website
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
